<script setup lang="ts">
import { computed } from 'vue';
import { NIcon } from '@nio-fe/icon';
import IconMateMicroDefault from '@/assets/images/icon_mate_micro_default.svg';

interface MicroAppListItemProps {
  icon: string;
  title: string;
  activated?: boolean;
  size?: string;
  mode?: string;
  disableTooltip?: boolean;
}

const props = withDefaults(defineProps<MicroAppListItemProps>(), {
  icon: 'task',
  mode: 'normal',
  size: 'normal',
});

const isFontIcon = computed(() => {
  return props.icon && !props.icon.startsWith('http');
});

const fontIconName = computed(() => {
  if (!props.icon) {
    return '';
  } else if (props.icon.startsWith('rz-') || props.icon.startsWith('n-')) {
    return '';
  } else {
    return props.icon;
  }
});

const shouldDisableTooltip = computed(() => {
  if (props.disableTooltip) {
    return true;
  }
  if (props.title.length < 5) {
    return true;
  }
  if (props.activated && props.title.length < 3) {
    return true;
  }
  return false;
});
</script>

<template>
  <div
    :class="{
      'micro-app-list-item': true,
      'micro-app-list-item--mini': size === 'mini',
      'micro-app-list-item--selected': activated,
      'micro-app-list-item--mode-drawer': mode === 'drawer',
    }"
    v-tooltip.right="{
      content: title,
      disabled: shouldDisableTooltip,
    }"
  >
    <div class="micro-app-list-item__left">
      <div class="micro-app-list-item__icon">
        <div v-if="isFontIcon" class="micro-app-list-item__fonticon">
          <n-icon
            v-if="fontIconName"
            :size="mode === 'drawer' ? 24 : 20"
            :name="fontIconName"
          />
          <img
            v-else
            :width="mode === 'drawer' ? 24 : 20"
            :src="IconMateMicroDefault"
            alt="DEFAULT_ICON"
          />
        </div>
        <img
          class="micro-app-list-item__imgicon"
          v-else
          :src="icon"
          alt="ICON"
        />
        <slot name="icon-affix"></slot>
      </div>
      <p class="micro-app-list-item__title">{{ title }}</p>
    </div>
    <div class="micro-app-list-item__action">
      <slot name="action"></slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.micro-app-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 8px;
  margin: 0 8px;
  cursor: pointer;
  margin-bottom: 8px;
  position: relative;

  &--mini {
    padding: 4px 0;
    margin: 0 4px 4px 4px;
    .micro-app-list-item__left {
      flex-direction: column;
      justify-content: center;
      width: 100%;
    }

    .micro-app-list-item__title {
      font-size: 12px;
      margin-left: 0;
      line-height: 18px;
      width: 100%;
      text-align: center;
    }

    .micro-app-list-item__action {
      position: absolute;
      right: 0;
      top: 0;
    }
  }

  &__action {
    display: none;
  }

  &:hover {
    background: var(--tit-sidebar-menu-background-hover);
    color: var(--tit-sidebar-menu-label-color-select);
    border-radius: 4px;
    .micro-app-list-item__title {
      color: var(--tit-sidebar-menu-label-color-select);
    }

    .micro-app-list-item__action {
      display: block;
    }
  }

  &--selected {
    background: var(--tit-sidebar-menu-background-select);
    border-radius: 4px;
    color: var(--tit-sidebar-menu-label-color-select);
    font-weight: bold;
    .micro-app-list-item__title {
      color: var(--tit-sidebar-menu-label-color-select);
    }
  }

  &__left {
    display: flex;
    align-items: center;
  }
  &__title {
    margin: 0;
    margin-left: 8px;
    font-size: 14px;
    line-height: 22px;
    color: var(--tit-sidebar-menu-label-color);
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  &__imgicon {
    display: block;
  }
  &__fonticon {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    //background: linear-gradient(180deg, #6bc6ff 0%, #0a6ecc 100%);

    :deep(.n-icon) {
      color: var(--tit-sidebar-menu-icon-color);
    }
  }

  &__icon {
    position: relative;
    .micro-app-list-item__imgicon {
      width: 20px;
    }
  }

  &--mode-drawer {
    padding: 6px;
    margin: 12px 0;
    &:hover {
      background: rgba(156, 159, 172, 0.15);

      .micro-app-list-item__title {
        color: #040b29;
      }
    }

    .micro-app-list-item__title {
      color: #040b29;
      font-size: 14px;
      margin-left: 8px;
    }

    .micro-app-list-item__fonticon {
      width: 40px;
      height: 40px;
      background: #f3f3f6;

      :deep(.n-icon) {
        color: rgba(23, 140, 166, 1);
      }
    }
    .micro-app-list-item__icon {
      .micro-app-list-item__imgicon {
        width: 40px;
      }
    }
  }
}
</style>
